import { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabaseClient";
import { ArrowLeft, Plus, Trash2, RefreshCw, FileText, Edit2, X } from "lucide-react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const GROUP_TYPES = ["Bank Deposit", "Corporate Deposit", "Government", "Equity", "Cash"];

const INSTRUMENT_MAP = {
  "Bank Deposit": ["Term Deposit", "Certificate of Deposit"],
  "Corporate Deposit": ["Commercial Paper", "Non Convertible Debenture"],
  "Government": ["Government Securities (GSec)", "Treasury Bills (T-Bills)"],
  "Equity": ["Mutual Fund", "Stock"],
  "Cash": ["Current Account", "Savings Account", "Overnight Deposit"],
};

const GROUP_COLORS = {
  "Bank Deposit": "#6366f1",
  "Corporate Deposit": "#22d3ee",
  "Government": "#10b981",
  "Equity": "#f59e0b",
  "Cash": "#8b5cf6",
};

const STATUS_OPTIONS = ["Active", "Matured", "Callable", "Non-Callable", "Redeemed"];

function formatDateInput(value) {
  const digits = value.replace(/\D/g, "");
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0,2)}-${digits.slice(2)}`;
  return `${digits.slice(0,2)}-${digits.slice(2,4)}-${digits.slice(4,8)}`;
}

function toISO(ddmmyyyy) {
  if (!ddmmyyyy || ddmmyyyy.length !== 10) return null;
  const [dd, mm, yyyy] = ddmmyyyy.split("-");
  return `${yyyy}-${mm}-${dd}`;
}

function fromISO(isoDate) {
  if (!isoDate) return "";
  const [yyyy, mm, dd] = isoDate.split("-");
  return `${dd}-${mm}-${yyyy}`;
}

function daysUntil(isoDate) {
  if (!isoDate) return null;
  const today = new Date(); today.setHours(0,0,0,0);
  const target = new Date(isoDate);
  return Math.round((target - today) / (1000 * 60 * 60 * 24));
}

function formatAmount(n) {
  if (!n && n !== 0) return "₹0";
  if (Math.abs(n) >= 10000000) return `₹${(n/10000000).toFixed(2)}Cr`;
  if (Math.abs(n) >= 100000)   return `₹${(n/100000).toFixed(2)}L`;
  if (Math.abs(n) >= 1000)     return `₹${(n/1000).toFixed(2)}K`;
  return `₹${n.toFixed(0)}`;
}

const RADIAN = Math.PI / 180;
function CustomLabel({ cx, cy, midAngle, innerRadius, outerRadius, percent }) {
  if (percent < 0.05) return null;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight="600">
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
}

export default function Investments({ onBack, companyId }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showTargetForm, setShowTargetForm] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [liveRates, setLiveRates] = useState({ gsec: 6.93, tbill: 5.33 }); // latest known fallback
const [ratesLoading, setRatesLoading] = useState(false);
const [ratesUpdatedAt, setRatesUpdatedAt] = useState(null);
  const [topMF, setTopMF] = useState([]);
  const [topFDs, setTopFDs] = useState([]);
  const [targetAllocation, setTargetAllocation] = useState({
    "Bank Deposit": 30, "Corporate Deposit": 20,
    "Government": 25, "Equity": 15, "Cash": 10
  });

  const [form, setForm] = useState({
    group_type: "", instrument_type: "", instrument_name: "",
    placed_with: "", broker: "", amount: "",
    start_date: "", end_date: "", rate: "",
    nav: "", stock_price: "", quantity: "", callable: false, status: "Active",
  });

  const resetForm = () => setForm({
    group_type: "", instrument_type: "", instrument_name: "",
    placed_with: "", broker: "", amount: "",
    start_date: "", end_date: "", rate: "",
    nav: "", stock_price: "", quantity: "", callable: false, status: "Active",
  });

  const loadRecords = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('investments')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error("Error loading investments:", error);
      } else {
        setRecords(data || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { loadRecords(); }, [loadRecords]);

  const fetchLiveRates = useCallback(async () => {
  setRatesLoading(true);
  try {
    const [gsecRes, tbillRes] = await Promise.all([
      fetch("https://query1.finance.yahoo.com/v8/finance/chart/%5ETNYTRI?interval=1d&range=1d"),
      fetch("https://query1.finance.yahoo.com/v8/finance/chart/IN10Y%3DRR?interval=1d&range=1d"),
    ]);
    // Yahoo Finance tickers for India bonds are unreliable,
    // so use stooq.com which has IN10Y and IN3M (91-day approx)
    const [gsecStooq, tbillStooq] = await Promise.all([
      fetch("https://stooq.com/q/l/?s=10iny.b&f=l&e=json"),
      fetch("https://stooq.com/q/l/?s=91iny.b&f=l&e=json"),
    ]);
    const gsecData = await gsecStooq.json();
    const tbillData = await tbillStooq.json();

    const gsec = parseFloat(gsecData?.symbols?.[0]?.close ?? gsecData?.close);
    const tbill = parseFloat(tbillData?.symbols?.[0]?.close ?? tbillData?.close);

    if (!isNaN(gsec) && !isNaN(tbill)) {
      setLiveRates({
        gsec: gsec.toFixed(2),
        tbill: tbill.toFixed(2),
      });
      setRatesUpdatedAt(new Date());
    }
  } catch (err) {
    console.warn("Live rates fetch failed, using fallback:", err);
    // fallback stays as initialized state
  } finally {
    setRatesLoading(false);
  }
}, []);

useEffect(() => {
  fetchLiveRates();
  const interval = setInterval(fetchLiveRates, 5 * 60 * 1000); // refresh every 5 min
  return () => clearInterval(interval);
}, [fetchLiveRates]);

  useEffect(() => {
    setTopFDs([
      { bank: "HDFC Bank", rate: 6.40, tenure: "2 Year" },
{ bank: "SBI", rate: 6.40, tenure: "2 Year" },
{ bank: "ICICI Bank", rate: 6.50, tenure: "2 Year" },
{ bank: "Axis Bank", rate: 6.45, tenure: "2 Year" },
{ bank: "Kotak Bank", rate: 6.70, tenure: "15M–3Y" },
    ]);
    setTopMF([
     { name: "Nippon India Large Cap", category: "Large Cap", returns1Y: 9.5, returns3Y: 18.2 },
{ name: "ICICI Pru Large Cap", category: "Large Cap", returns1Y: 7.0, returns3Y: 16.8 },
{ name: "Invesco India Mid Cap", category: "Mid Cap", returns1Y: 6.5, returns3Y: 25.1 },
{ name: "Motilal Oswal Midcap", category: "Mid Cap", returns1Y: -9.6, returns3Y: 20.2 },
{ name: "Nippon India Small Cap", category: "Small Cap", returns1Y: 10.0, returns3Y: 20.9 },
{ name: "SBI Small Cap Fund", category: "Small Cap", returns1Y: 2.5, returns3Y: 12.5 },
    ]);
  }, []);

  // ── KPI Calculations ──
  const activeRecords = records.filter(r => r.status !== "Redeemed");
  const totalPortfolio = activeRecords.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
    const debtRecords = activeRecords.filter(r => 
  r.instrument_type !== "Mutual Fund" && r.instrument_type !== "Stock"
);
const debtPortfolio = debtRecords.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
const weightedReturn = debtRecords.length > 0 && debtPortfolio > 0
  ? debtRecords.reduce((sum, r) => sum + ((parseFloat(r.rate) || 0) * (parseFloat(r.amount) || 0)), 0) / debtPortfolio
  : 0;
  const maturingIn30 = records.filter(r => {
    const d = daysUntil(r.end_date);
    return d !== null && d >= 0 && d <= 30 && r.status !== "Redeemed";
  }).length;

  // ── Pie Chart Data ──
  const pieData = GROUP_TYPES.map(g => ({
    name: g,
    value: records.filter(r => r.group_type === g && r.status !== "Redeemed").reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0),
    color: GROUP_COLORS[g],
  })).filter(d => d.value > 0);

  const handleSave = async () => {
    if (!form.group_type) { alert("Please select a group."); return; }
    if (!form.instrument_type) { alert("Please select an instrument type."); return; }
    if (!form.placed_with) { alert("Please enter the institution."); return; }
    if (!form.amount) { alert("Please enter the amount."); return; }
    if (!form.start_date || form.start_date.length !== 10) { alert("Please enter a valid start date in DD-MM-YYYY format."); return; }
    
    setSaving(true);
    const payload = {
      company_id: companyId,
      group_type: form.group_type,
      instrument_type: form.instrument_type,
      instrument_name: form.instrument_name,
      placed_with: form.placed_with,
      broker: form.broker,
      amount: parseFloat(form.amount) || null,
      start_date: toISO(form.start_date),
      end_date: form.end_date ? toISO(form.end_date) : null,
      rate: form.rate ? parseFloat(form.rate) : null,
      nav: form.nav ? parseFloat(form.nav) : null,
      stock_price: form.stock_price ? parseFloat(form.stock_price) : null,
      quantity: form.quantity ? parseFloat(form.quantity) : null,
      callable: form.callable,
      status: form.status,
    };

    try {
      if (editRecord) {
        const { error } = await supabase
          .from('investments')
          .update(payload)
          .eq('id', editRecord.id);

        if (error) console.error("Update error:", error);
      } else {
        const { error } = await supabase
          .from('investments')
          .insert(payload);

        if (error) console.error("Insert error:", error);
      }

      await loadRecords();
      resetForm();
      setShowForm(false);
      setEditRecord(null);
    } catch (e) {
      alert("Something went wrong.");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (r) => {
    setEditRecord(r);
    setForm({
      group_type: r.group_type || "",
      instrument_type: r.instrument_type || "",
      instrument_name: r.instrument_name || "",
      placed_with: r.placed_with || "",
      broker: r.broker || "",
      amount: r.amount || "",
      start_date: fromISO(r.start_date) || "",
      end_date: fromISO(r.end_date) || "",
      rate: r.rate || "",
      nav: r.nav || "",
      stock_price: r.stock_price || "",
      quantity: r.quantity || "",
      callable: r.callable || false,
      status: r.status || "Active",
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this investment?")) return;
    
    const { error } = await supabase
      .from('investments')
      .delete()
      .eq('id', id);

    if (error) {
      console.error("Delete error:", error);
    } else {
      await loadRecords();
    }
  };

  const generatePDF = async () => {
    setGeneratingPDF(true);
    try {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const dateStr = yesterday.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
      const doc = new jsPDF();
      const fmtPDF = (n) => {
  const num = parseFloat(String(n).replace(/[^0-9.]/g, "")) || 0;
  if (num >= 10000000) return `Rs ${(num / 10000000).toFixed(2)} Cr`;
  if (num >= 100000)   return `Rs ${(num / 100000).toFixed(2)} L`;
  if (num >= 1000)     return `Rs ${(num / 1000).toFixed(2)} K`;
  return `Rs ${num.toFixed(0)}`;
};
      const pageWidth = doc.internal.pageSize.getWidth();

      doc.setFillColor(10, 11, 15);
      doc.rect(0, 0, pageWidth, 40, "F");
      doc.setTextColor(99, 102, 241);
      doc.setFontSize(20);
      doc.setFont("helvetica", "bold");
      doc.text("NEBULA FINANCE AI", pageWidth / 2, 15, { align: "center" });
      doc.setFontSize(12);
      doc.setTextColor(200, 200, 200);
      doc.text("Investment Portfolio Report", pageWidth / 2, 24, { align: "center" });
      doc.setFontSize(9);
      doc.setTextColor(150, 150, 150);
      doc.text(`As of: ${dateStr}   |   Generated: ${new Date().toLocaleString("en-IN")}`, pageWidth / 2, 32, { align: "center" });

      let y = 50;

      doc.setFontSize(12); doc.setFont("helvetica", "bold"); doc.setTextColor(99, 102, 241);
      doc.text("PORTFOLIO SUMMARY", 14, y); y += 8;
      autoTable(doc, {
        startY: y,
        head: [["Metric", "Value"]],
        body: [
          ["Total Portfolio Value", fmtPDF(totalPortfolio)],
          ["Weighted Average Return", `${weightedReturn.toFixed(2)}%`],
          ["Active Investments", activeRecords.length.toString()],
          ["Maturing within 30 days", maturingIn30.toString()],
        ],
        styles: { fontSize: 10, cellPadding: 4 },
        headStyles: { fillColor: [99, 102, 241], textColor: 255, fontStyle: "bold" },
        alternateRowStyles: { fillColor: [245, 245, 255] },
        margin: { left: 14, right: 14 },
      });
      y = doc.lastAutoTable.finalY + 12;

      doc.setFontSize(12); doc.setFont("helvetica", "bold"); doc.setTextColor(99, 102, 241);
      doc.text("ASSET ALLOCATION", 14, y); y += 8;
      autoTable(doc, {
        startY: y,
        head: [["Group", "Amount", "Actual %", "Target %", "Deviation"]],
        body: GROUP_TYPES.map(g => {
          const val = records.filter(r => r.group_type === g && r.status !== "Redeemed").reduce((s, r) => s + (parseFloat(String(r.amount).replace(/[^0-9.]/g, ""))||0), 0);
          const pct = totalPortfolio > 0 ? ((val/totalPortfolio)*100).toFixed(1) : "0.0";
          const target = targetAllocation[g] || 0;
          const diff = parseFloat(pct) - target;
          return [g, fmtPDF (val), `${pct}%`, `${target}%`, diff > 0 ? `+${diff.toFixed(1)}%` : `${diff.toFixed(1)}%`];
        }),
        styles: { fontSize: 10, cellPadding: 4 },
        headStyles: { fillColor: [99, 102, 241], textColor: 255, fontStyle: "bold" },
        alternateRowStyles: { fillColor: [245, 245, 255] },
        margin: { left: 14, right: 14 },
      });
      y = doc.lastAutoTable.finalY + 12;

      doc.setFontSize(12); doc.setFont("helvetica", "bold"); doc.setTextColor(99, 102, 241);
      doc.text("INVESTMENT DETAILS", 14, y); y += 8;
      autoTable(doc, {
        startY: y,
        head: [["Group", "Instrument", "Institution", "Amount", "Start", "Maturity", "Rate/NAV", "Status"]],
        body: records.filter(r => r.status !== "Redeemed").map(r => [
          r.group_type || "", r.instrument_type || "", r.placed_with || "",
          fmtPDF (parseFloat(String(r.amount).replace(/[^0-9.]/g, "")) || 0), fromISO(r.start_date) || "",
          fromISO(r.end_date) || "N/A",
          r.instrument_type === "Mutual Fund" ? `NAV: Rs ${r.nav || "N/A"}` :
r.instrument_type === "Stock" ? `Rs ${r.stock_price || "N/A"}` :
r.rate ? `${r.rate}%` : "N/A",
          r.status || "",
        ]),
        styles: { fontSize: 8, cellPadding: 3 },
        headStyles: { fillColor: [99, 102, 241], textColor: 255, fontStyle: "bold" },
        alternateRowStyles: { fillColor: [245, 245, 255] },
        margin: { left: 14, right: 14 },
      });
      y = doc.lastAutoTable.finalY + 12;

      if (y > 240) { doc.addPage(); y = 20; }
      doc.setFontSize(12); doc.setFont("helvetica", "bold"); doc.setTextColor(99, 102, 241);
      doc.text("MARKET RATES", 14, y); y += 8;
      autoTable(doc, {
        startY: y,
        head: [["Instrument", "Rate", "Type"]],
        body: [
          ["10 Year GSec", `${liveRates.gsec}%`, "India Sovereign Bond"],
          ["91 Day T-Bill", `${liveRates.tbill}%`, "India Treasury Bill"],
          ...topFDs.map(f => [f.bank, `${f.rate}%`, `Fixed Deposit (${f.tenure})`]),
        ],
        styles: { fontSize: 10, cellPadding: 4 },
        headStyles: { fillColor: [99, 102, 241], textColor: 255, fontStyle: "bold" },
        alternateRowStyles: { fillColor: [245, 245, 255] },
        margin: { left: 14, right: 14 },
      });

      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8); doc.setTextColor(150, 150, 150);
        doc.text("DISCLAIMER: This report is for informational purposes only. Nebula Finance AI — Confidential",
          pageWidth / 2, doc.internal.pageSize.getHeight() - 8, { align: "center" });
        doc.text(`Page ${i} of ${pageCount}`, pageWidth - 14, doc.internal.pageSize.getHeight() - 8, { align: "right" });
      }

      doc.save(`Investment_Report_${dateStr.replace(/ /g, "_")}.pdf`);
    } catch (e) {
      console.error(e);
      alert("Error generating PDF.");
    } finally {
      setGeneratingPDF(false);
    }
  };

  const instruments = form.group_type ? (INSTRUMENT_MAP[form.group_type] || []) : [];
  const isDebt = form.group_type !== "Equity" && form.group_type !== "Cash";

  return (
    <div className="max-w-screen-xl mx-auto px-6 py-6">

      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={onBack} className="p-2 rounded-xl bg-[#1a1d26] border border-[#2a2d3a] text-gray-400 hover:text-white transition-all">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h2 className="text-2xl font-bold text-white">Investment Dashboard</h2>
          <p className="text-gray-400 text-sm">Portfolio overview and performance tracking</p>
        </div>
        <div className="ml-auto flex items-center gap-3 flex-wrap">
          <button onClick={loadRecords} className="p-2 rounded-xl bg-[#1a1d26] border border-[#2a2d3a] text-gray-400 hover:text-white transition-all">
            <RefreshCw className="w-4 h-4" />
          </button>
          <button onClick={() => setShowTargetForm(!showTargetForm)} className="flex items-center gap-2 px-4 py-2 rounded-xl border border-teal-500/50 text-teal-400 hover:bg-teal-500/10 transition-all text-sm font-medium">
            🎯 Set Target
          </button>
          <button onClick={generatePDF} disabled={generatingPDF} className="flex items-center gap-2 px-4 py-2 rounded-xl border border-purple-500/50 text-purple-400 hover:bg-purple-500/10 transition-all text-sm font-medium">
            <FileText className="w-4 h-4" />
            {generatingPDF ? "Generating..." : "Generate Report"}
          </button>
          <button onClick={() => { setShowForm(!showForm); setEditRecord(null); resetForm(); }} className="nebula-btn-primary flex items-center gap-2 text-sm px-4 py-2">
            <Plus className="w-4 h-4" /> Add Investment
          </button>
        </div>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="nebula-card p-4 text-center">
          <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Total Portfolio</p>
          <p className="text-2xl font-bold text-white">{formatAmount(totalPortfolio)}</p>
        </div>
        <div className="nebula-card p-4 text-center">
          <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Wtd Avg Return (Debt)</p>
          <p className="text-2xl font-bold text-emerald-400">{weightedReturn.toFixed(2)}%</p>
        </div>
        <div className="nebula-card p-4 text-center">
          <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Active Investments</p>
          <p className="text-2xl font-bold text-indigo-400">{activeRecords.length}</p>
        </div>
        <div className="nebula-card p-4 text-center">
          <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Maturing in 30d</p>
          <p className={`text-2xl font-bold ${maturingIn30 > 0 ? "text-amber-400" : "text-gray-400"}`}>{maturingIn30}</p>
        </div>
      </div>

      {/* Target Allocation Form */}
      {showTargetForm && (
        <div className="nebula-card p-6 mb-6 border border-teal-500/20">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold text-white">Set Target Allocation</h3>
              <p className="text-gray-400 text-sm">Total must add up to 100%. Current: <span className={`font-semibold ${Object.values(targetAllocation).reduce((a,b) => a+b, 0) === 100 ? "text-emerald-400" : "text-red-400"}`}>{Object.values(targetAllocation).reduce((a,b) => a+b, 0)}%</span></p>
            </div>
            <button onClick={() => setShowTargetForm(false)} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {GROUP_TYPES.map(g => (
              <div key={g}>
                <label className="block text-xs font-medium mb-1" style={{ color: GROUP_COLORS[g] }}>{g}</label>
                <div className="relative">
                  <input type="number" min="0" max="100" value={targetAllocation[g]}
                    onChange={e => setTargetAllocation(prev => ({ ...prev, [g]: parseFloat(e.target.value) || 0 }))}
                    className="nebula-input text-sm pr-8" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">%</span>
                </div>
              </div>
            ))}
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={() => {
              const total = Object.values(targetAllocation).reduce((a,b) => a+b, 0);
              if (total !== 100) { alert(`Total must be exactly 100%. Currently ${total}%.`); return; }
              setShowTargetForm(false);
            }} className="nebula-btn-primary text-sm px-6 py-2">Save Target</button>
            <button onClick={() => setTargetAllocation({ "Bank Deposit": 30, "Corporate Deposit": 20, "Government": 25, "Equity": 15, "Cash": 10 })}
              className="px-6 py-2 rounded-xl border border-[#2a2d3a] text-gray-400 hover:text-white text-sm transition-all">Reset to Default</button>
          </div>
        </div>
      )}

      {/* Add / Edit Form */}
      {showForm && (
        <div className="nebula-card p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-white">{editRecord ? "Edit Investment" : "Add New Investment"}</h3>
            <button onClick={() => { setShowForm(false); resetForm(); setEditRecord(null); }} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Group <span className="text-red-400">*</span></label>
              <select value={form.group_type} onChange={e => setForm({...form, group_type: e.target.value, instrument_type: ""})} className="nebula-input text-sm">
                <option value="">Select group...</option>
                {GROUP_TYPES.map(g => <option key={g}>{g}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Instrument Type <span className="text-red-400">*</span></label>
              <select value={form.instrument_type} onChange={e => setForm({...form, instrument_type: e.target.value})} className="nebula-input text-sm" disabled={!form.group_type}>
                <option value="">Select instrument...</option>
                {instruments.map(i => <option key={i}>{i}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Instrument Name / Ticker</label>
              <input value={form.instrument_name} onChange={e => setForm({...form, instrument_name: e.target.value})} className="nebula-input text-sm" placeholder="e.g. HDFC FD / NIFTY 50 ETF" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Placed With / Institution <span className="text-red-400">*</span></label>
              <input value={form.placed_with} onChange={e => setForm({...form, placed_with: e.target.value})} className="nebula-input text-sm" placeholder="e.g. HDFC Bank" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Broker</label>
              <input value={form.broker} onChange={e => setForm({...form, broker: e.target.value})} className="nebula-input text-sm" placeholder="e.g. Zerodha" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Amount (₹) <span className="text-red-400">*</span></label>
              <input type="number" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} className="nebula-input text-sm" placeholder="e.g. 1000000" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Start Date (DD-MM-YYYY) <span className="text-red-400">*</span></label>
              <input value={form.start_date} onChange={e => setForm({...form, start_date: formatDateInput(e.target.value)})} className="nebula-input text-sm" placeholder="DD-MM-YYYY" maxLength={10} />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">End / Maturity Date (DD-MM-YYYY)</label>
              <input value={form.end_date} onChange={e => setForm({...form, end_date: formatDateInput(e.target.value)})} className="nebula-input text-sm" placeholder="DD-MM-YYYY" maxLength={10} />
            </div>
            {isDebt && form.instrument_type !== "Mutual Fund" && form.instrument_type !== "Stock" && (
              <div>
                <label className="block text-xs text-gray-400 mb-1">Interest Rate % p.a.</label>
                <input type="number" step="0.01" value={form.rate} onChange={e => setForm({...form, rate: e.target.value})} className="nebula-input text-sm" placeholder="e.g. 7.5" />
              </div>
            )}
            {form.instrument_type === "Mutual Fund" && (
              <>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">NAV (₹)</label>
                  <input type="number" step="0.01" value={form.nav} onChange={e => setForm({...form, nav: e.target.value})} className="nebula-input text-sm" placeholder="e.g. 45.23" />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Units</label>
                  <input type="number" step="0.001" value={form.quantity} onChange={e => setForm({...form, quantity: e.target.value})} className="nebula-input text-sm" placeholder="e.g. 1000.5" />
                </div>
              </>
            )}
            {form.instrument_type === "Stock" && (
              <>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Stock Price (₹)</label>
                  <input type="number" step="0.01" value={form.stock_price} onChange={e => setForm({...form, stock_price: e.target.value})} className="nebula-input text-sm" placeholder="e.g. 1520.50" />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Quantity (Shares)</label>
                  <input type="number" value={form.quantity} onChange={e => setForm({...form, quantity: e.target.value})} className="nebula-input text-sm" placeholder="e.g. 100" />
                </div>
              </>
            )}
            <div>
              <label className="block text-xs text-gray-400 mb-1">Status</label>
              <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="nebula-input text-sm">
                {STATUS_OPTIONS.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            {isDebt && (
              <div className="flex items-center gap-3 mt-4">
                <input type="checkbox" id="callable" checked={form.callable} onChange={e => setForm({...form, callable: e.target.checked})} className="w-4 h-4 accent-indigo-500" />
                <label htmlFor="callable" className="text-sm text-gray-300">Callable before maturity</label>
              </div>
            )}
          </div>
          <div className="flex gap-3 mt-5">
            <button onClick={handleSave} disabled={saving} className="nebula-btn-primary text-sm px-6 py-2 flex items-center gap-2">
              {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : editRecord ? "Update" : "Save"}
            </button>
            <button onClick={() => { setShowForm(false); resetForm(); setEditRecord(null); }} className="px-6 py-2 rounded-xl border border-[#2a2d3a] text-gray-400 hover:text-white text-sm transition-all">Cancel</button>
          </div>
        </div>
      )}

      {/* Main Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">

        {/* Pie Chart */}
        <div className="nebula-card p-6">
          <h3 className="text-lg font-bold text-white mb-4">Portfolio Allocation</h3>
          {pieData.length === 0 ? (
            <div className="text-center py-12 text-gray-500 text-sm">Add investments to see portfolio allocation.</div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <defs>
                    {pieData.map((entry, i) => (
                      <radialGradient key={i} id={`grad${i}`} cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor={entry.color} stopOpacity={1} />
                        <stop offset="100%" stopColor={entry.color} stopOpacity={0.6} />
                      </radialGradient>
                    ))}
                  </defs>
                  <Pie data={pieData} cx="50%" cy="50%" outerRadius={120} innerRadius={0}
                    paddingAngle={2} dataKey="value" labelLine={false} label={CustomLabel}
                    stroke="none" style={{ filter: "drop-shadow(0px 8px 16px rgba(0,0,0,0.4))" }}>
                    {pieData.map((entry, i) => (
                      <Cell key={i} fill={`url(#grad${i})`} stroke={entry.color} strokeWidth={1} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(val) => formatAmount(val)} contentStyle={{ background: "#12141a", border: "1px solid #2a2d3a", borderRadius: 8, fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2 mt-2">
                {pieData.map((d, i) => {
                  const pct = totalPortfolio > 0 ? ((d.value / totalPortfolio) * 100).toFixed(1) : 0;
                  const target = targetAllocation[d.name] || 0;
                  const diff = parseFloat(pct) - target;
                  return (
                    <div key={i} className="flex items-center gap-3">
                      <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: d.color }} />
                      <span className="text-xs text-gray-300 flex-1">{d.name}</span>
                      <span className="text-xs text-gray-400">{formatAmount(d.value)}</span>
                      <span className="text-xs font-semibold" style={{ color: d.color }}>{pct}%</span>
                      <span className={`text-xs ${diff > 0 ? "text-amber-400" : diff < 0 ? "text-blue-400" : "text-emerald-400"}`}>
                        {diff > 0 ? `+${diff.toFixed(1)}%` : `${diff.toFixed(1)}%`}
                      </span>
                    </div>
                  );
                })}
                <p className="text-xs text-gray-600 mt-1">% deviation from target allocation</p>
              </div>
            </>
          )}
        </div>

        {/* Investment Details Table */}
        <div className="nebula-card overflow-hidden">
          <div className="px-6 py-4 border-b border-[#2a2d3a] flex items-center justify-between">
            <h3 className="text-lg font-bold text-white">Investment Details</h3>
            <span className="text-xs text-gray-500">{activeRecords.length} active</span>
          </div>
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : records.length === 0 ? (
            <div className="text-center py-12 text-gray-500 text-sm">No investments yet.</div>
          ) : (
            <div className="overflow-auto max-h-96">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-[#12141a]">
                  <tr className="border-b border-[#2a2d3a]">
                    {["Group", "Instrument", "Amount", "Maturity", "Rate/NAV", "Status", ""].map(h => (
                      <th key={h} className="text-left text-xs text-gray-500 uppercase py-3 px-3 font-medium whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {records.map((r, i) => {
                    const days = daysUntil(r.end_date);
                    return (
                      <tr key={r.id} className={`border-b border-[#2a2d3a]/40 ${i % 2 === 0 ? "" : "bg-[#12141a]/40"}`}>
                        <td className="py-2 px-3">
                          <span className="px-1.5 py-0.5 rounded text-xs font-medium" style={{ background: GROUP_COLORS[r.group_type] + "20", color: GROUP_COLORS[r.group_type] }}>
                            {r.group_type?.split(" ")[0]}
                          </span>
                        </td>
                        <td className="py-2 px-3">
                          <p className="text-white text-xs font-medium">{r.instrument_type}</p>
                          <p className="text-gray-500 text-xs">{r.placed_with}</p>
                        </td>
                        <td className="py-2 px-3 text-white font-semibold text-xs">{formatAmount(parseFloat(r.amount))}</td>
                        <td className="py-2 px-3">
                          <p className="text-gray-400 text-xs">{fromISO(r.end_date) || "—"}</p>
                          {days !== null && days >= 0 && days <= 30 && <span className="text-xs text-red-400">{days}d</span>}
                        </td>
                        <td className="py-2 px-3 text-xs">
  {r.instrument_type === "Mutual Fund" ? (
    <span className="text-blue-400">NAV: ₹{r.nav || "—"}</span>
  ) : r.instrument_type === "Stock" ? (
    <span className="text-amber-400">₹{r.stock_price || "—"}</span>
  ) : r.rate ? (
    <span className="text-emerald-400">{r.rate}%</span>
  ) : "—"}
</td>
                        <td className="py-2 px-3">
                          <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                            r.status === "Active" ? "bg-emerald-500/20 text-emerald-400" :
                            r.status === "Matured" ? "bg-blue-500/20 text-blue-400" :
                            r.status === "Redeemed" ? "bg-gray-500/20 text-gray-400" :
                            "bg-amber-500/20 text-amber-400"
                          }`}>{r.status}</span>
                        </td>
                        <td className="py-2 px-3">
                          <div className="flex items-center gap-1">
                            <button onClick={() => handleEdit(r)} className="text-indigo-400 hover:text-indigo-300 p-1"><Edit2 className="w-3 h-3" /></button>
                            <button onClick={() => handleDelete(r.id)} className="text-red-400 hover:text-red-300 p-1"><Trash2 className="w-3 h-3" /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Top Performing MFs */}
        <div className="nebula-card p-6">
          <h3 className="text-lg font-bold text-white mb-4">Top Performing Mutual Funds</h3>
          {["Large Cap", "Mid Cap", "Small Cap"].map(cat => (
            <div key={cat} className="mb-4">
              <p className="text-xs font-semibold text-indigo-400 uppercase tracking-wider mb-2">{cat}</p>
              <div className="space-y-2">
                {topMF.filter(m => m.category === cat).map((m, i) => (
                  <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-[#0a0b0f] border border-[#2a2d3a]">
                    <div>
                      <p className="text-sm text-white font-medium">{m.name}</p>
                      <p className="text-xs text-gray-500">3Y: {m.returns3Y}%</p>
                    </div>
                    <div className="text-right">
                      <p className="text-emerald-400 font-bold text-sm">{m.returns1Y}%</p>
                      <p className="text-xs text-gray-500">1Y Return</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          <p className="text-xs text-gray-600 mt-2">* Returns are indicative. Past performance does not guarantee future returns.</p>
        </div>

        {/* Live Market Rates */}
        <div className="nebula-card p-6">
          <div className="flex items-center justify-between mb-4">
  <h3 className="text-lg font-bold text-white">Live Market Rates</h3>
  <div className="flex items-center gap-2">
    {ratesLoading ? (
      <div className="w-3 h-3 border border-emerald-500 border-t-transparent rounded-full animate-spin" />
    ) : (
      <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
    )}
    <span className="text-xs text-gray-500">
      {ratesUpdatedAt
        ? `Updated ${ratesUpdatedAt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}`
        : "Fetching..."}
    </span>
    <button onClick={fetchLiveRates} className="text-gray-500 hover:text-white transition-all">
      <RefreshCw className="w-3 h-3" />
    </button>
  </div>
</div>
          <div className="grid grid-cols-2 gap-3 mb-5">
            <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-center">
              <p className="text-xs text-gray-400 mb-1">10 Year GSec</p>
              <p className="text-2xl font-bold text-emerald-400">{liveRates.gsec}%</p>
              <p className="text-xs text-gray-500 mt-1">India Sovereign Bond</p>
            </div>
            <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/20 text-center">
              <p className="text-xs text-gray-400 mb-1">91 Day T-Bill</p>
              <p className="text-2xl font-bold text-blue-400">{liveRates.tbill}%</p>
              <p className="text-xs text-gray-500 mt-1">India Treasury Bill</p>
            </div>
          </div>
          <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">Top Fixed Deposit Rates</h4>
          <div className="space-y-2">
            {topFDs.map((fd, i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-[#0a0b0f] border border-[#2a2d3a]">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-indigo-500/20 flex items-center justify-center">
                    <span className="text-indigo-400 text-xs font-bold">{i+1}</span>
                  </div>
                  <div>
                    <p className="text-sm text-white font-medium">{fd.bank}</p>
                    <p className="text-xs text-gray-500">{fd.tenure}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-amber-400 font-bold">{fd.rate}%</p>
                  <p className="text-xs text-gray-500">p.a.</p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-600 mt-3">* Rates are indicative. Verify with respective banks.</p>
        </div>
      </div>
    </div>
  );
}
